import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { Map, type MapRef, type ViewStateChangeEvent } from "react-map-gl/mapbox";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { getMapboxConfig } from "~/lib/config";
import { DEFAULT_MAP_CENTER, MAP_MAX_ZOOM, setupMapTouchContextMenu } from "~/lib/mapConstants";
import type { PlaceGeoJSON } from "~/lib/api";

type Padding = number | mapboxgl.PaddingOptions;

interface NomaMapProps {
  /** GeoJSON to fit bounds to, or explicit LngLatBounds */
  bounds?: PlaceGeoJSON | mapboxgl.LngLatBoundsLike;
  /** Padding for fit bounds */
  padding?: Padding;
  /** Explicit center (used if bounds not provided) */
  center?: [number, number];
  /** Explicit zoom (used if bounds not provided) */
  zoom?: number;
  /** Whether map interactions are enabled (default: true) */
  interactive?: boolean;
  /** Map click handler */
  onClick?: (e: mapboxgl.MapMouseEvent) => void;
  /** Context menu handler (right-click on desktop, long-press on mobile) */
  onContextMenu?: (e: mapboxgl.MapMouseEvent) => void;
  /** Called when map finishes loading */
  onLoad?: (map: mapboxgl.Map) => void;
  /** Called when map moves */
  onMove?: (e: ViewStateChangeEvent) => void;
  /** Additional CSS class for the map container */
  className?: string;
  /** Map children (layers, controls, etc.) */
  children?: ReactNode;
}

/**
 * Root map component wrapping react-map-gl with noma.to defaults.
 * Provides automatic bounds fitting, touch context menu support, and noma.to styling.
 */
export function NomaMap({
  bounds,
  padding,
  center,
  zoom,
  interactive = true,
  onClick,
  onContextMenu,
  onLoad,
  onMove,
  className = "w-full h-full",
  children,
}: NomaMapProps) {
  const mapRef = useRef<MapRef>(null);
  const mapboxConfig = getMapboxConfig();

  // Calculate initial view state from bounds or center/zoom
  const initialViewState = useMemo(() => {
    if (bounds) {
      const lngLatBounds = boundsFromGeoJSONOrBounds(bounds);
      if (lngLatBounds) {
        // react-map-gl doesn't support bounds in initialViewState directly,
        // so we'll fit bounds on load instead
        return {
          longitude: DEFAULT_MAP_CENTER[0],
          latitude: DEFAULT_MAP_CENTER[1],
          zoom: zoom ?? 8,
        };
      }
    }
    return {
      longitude: center?.[0] ?? DEFAULT_MAP_CENTER[0],
      latitude: center?.[1] ?? DEFAULT_MAP_CENTER[1],
      zoom: zoom ?? 8,
    };
  }, [bounds, center, zoom]);

  // Track cleanup function for touch context menu
  const touchContextMenuCleanup = useRef<(() => void) | null>(null);

  // Fit bounds on load and setup touch context menu
  const handleLoad = useCallback(
    (e: mapboxgl.MapboxEvent) => {
      const map = e.target;

      if (bounds) {
        const lngLatBounds = boundsFromGeoJSONOrBounds(bounds);
        if (lngLatBounds) {
          map.fitBounds(lngLatBounds, {
            padding: padding ?? 80,
            maxZoom: MAP_MAX_ZOOM,
            duration: 0, // Instant on initial load
          });
        }
      }

      // Setup touch context menu (long-press → contextmenu)
      if (onContextMenu) {
        touchContextMenuCleanup.current = setupMapTouchContextMenu(map);
      }

      onLoad?.(map);
    },
    [bounds, padding, onContextMenu, onLoad],
  );

  // Cleanup touch context menu on unmount
  useEffect(() => {
    return () => {
      touchContextMenuCleanup.current?.();
    };
  }, []);

  // Handle context menu
  const handleContextMenu = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      onContextMenu?.(e);
    },
    [onContextMenu],
  );

  return (
    <Map
      ref={mapRef}
      mapboxAccessToken={mapboxConfig.token}
      mapStyle={mapboxConfig.style}
      initialViewState={initialViewState}
      onLoad={handleLoad}
      onClick={interactive ? onClick : undefined}
      onContextMenu={interactive ? handleContextMenu : undefined}
      onMove={onMove}
      reuseMaps
      style={{ width: "100%", height: "100%" }}
      mapLib={mapboxgl}
      scrollZoom={interactive}
      boxZoom={interactive}
      dragRotate={interactive}
      dragPan={interactive}
      keyboard={interactive}
      doubleClickZoom={interactive}
      touchZoomRotate={interactive}
      touchPitch={interactive}
    >
      <div className={className}>{children}</div>
    </Map>
  );
}

/**
 * Converts GeoJSON FeatureCollection or LngLatBounds to mapboxgl.LngLatBounds.
 */
function boundsFromGeoJSONOrBounds(input: PlaceGeoJSON | mapboxgl.LngLatBoundsLike): mapboxgl.LngLatBounds | null {
  // Check if it's a GeoJSON FeatureCollection
  if (isGeoJSON(input)) {
    if (input.features.length === 0) return null;

    const validCoords = input.features
      .map((f) => f.geometry.coordinates)
      .filter(
        (c): c is [number, number] =>
          Array.isArray(c) &&
          c.length === 2 &&
          typeof c[0] === "number" &&
          typeof c[1] === "number" &&
          !isNaN(c[0]) &&
          !isNaN(c[1]),
      );

    if (validCoords.length === 0) return null;

    const bounds = new mapboxgl.LngLatBounds(validCoords[0], validCoords[0]);
    validCoords.slice(1).forEach((coord) => bounds.extend(coord));
    return bounds;
  }

  // Already LngLatBoundsLike
  return mapboxgl.LngLatBounds.convert(input);
}

function isGeoJSON(input: PlaceGeoJSON | mapboxgl.LngLatBoundsLike): input is PlaceGeoJSON {
  return typeof input === "object" && input !== null && "type" in input && input.type === "FeatureCollection";
}
